import React, {MouseEvent} from 'react';

import {Stack} from '@mui/material';

import {getScreenshotUrl} from '../../utils';

import {ScreenshotKeyConnection} from '@bumpy/i18n_common';

type Props = {
  connections: ScreenshotKeyConnection[];
  texts?: (string | undefined)[];
  focusedIndex?: number | null;
  projectUuid: string;
  screenshotName: string;
  onColorPick?: (color: string, final: boolean) => void;
  onFocusIndex?: (index: number | null) => void;
  onKeyPressed?: (key: string) => void;
  onConnectionsChange?: (connections: ScreenshotKeyConnection[]) => void;
};

type ResizeMode =
  | 'left'
  | 'topLeft'
  | 'top'
  | 'topRight'
  | 'right'
  | 'bottomRight'
  | 'bottom'
  | 'bottomLeft';

type ActiveState = {
  connection: ScreenshotKeyConnection | null;
  startTouchX: number | null;
  startTouchY: number | null;
  resizeMode: ResizeMode | null;
};

export const Screenshot: React.FC<Props> = (props: Props) => {
  const [active, setActive] = React.useState<ActiveState>({
    connection: null,
    startTouchX: null,
    startTouchY: null,
    resizeMode: null,
  });
  const canvasRef = React.useRef<CanvasRenderingContext2D>();

  function findConnection(
    x: number,
    y: number,
  ): ScreenshotKeyConnection | null {
    const gap = 3;
    for (const c of props.connections) {
      if (
        x + gap >= c.x &&
        x - gap <= c.x + c.width &&
        y + gap >= c.y &&
        y - gap <= c.y + c.height
      ) {
        return c;
      }
    }
    return null;
  }

  return (
    <div
      style={{position: 'relative'}}
      onMouseUp={() => {
        if (!props.onConnectionsChange) {
          return;
        }
        const connections = [...props.connections];

        if (active.connection) {
          if (active.connection.width < 40 && active.connection.height < 40) {
            connections.splice(connections.indexOf(active.connection), 1);
          }
        }
        setActive({
          connection: null,
          startTouchX: null,
          startTouchY: null,
          resizeMode: null,
        });
        props.onFocusIndex?.(null);
        props.onConnectionsChange!(connections);
      }}
      onMouseDown={(e: MouseEvent) => {
        e.preventDefault();
        const {offsetX, offsetY} = e.nativeEvent;
        if (canvasRef.current && props.onColorPick) {
          const pixel = canvasRef.current.getImageData(offsetX, offsetY, 1, 1);
          props.onColorPick(rgb(pixel), true);
        }
        let connection = findConnection(offsetX, offsetY);
        if (connection) {
          if (props.onKeyPressed) {
            props.onKeyPressed(connection.key);
          }
          if (props.onFocusIndex) {
            props.onFocusIndex(props.connections.indexOf(connection));
          }
        }
        if (!props.onConnectionsChange) {
          return;
        }

        let resizeMode: ResizeMode | null = null;
        if (connection) {
          const delta = 5;
          if (Math.abs(connection.x - offsetX) < delta) {
            if (Math.abs(connection.y - offsetY) < delta) {
              resizeMode = 'topLeft';
            } else if (
              Math.abs(connection.y + connection.height - offsetY) < delta
            ) {
              resizeMode = 'bottomLeft';
            } else {
              resizeMode = 'left';
            }
          } else if (
            Math.abs(connection.x + connection.width - offsetX) < delta
          ) {
            if (Math.abs(connection.y - offsetY) < delta) {
              resizeMode = 'topRight';
            } else if (
              Math.abs(connection.y + connection.height - offsetY) < delta
            ) {
              resizeMode = 'bottomRight';
            } else {
              resizeMode = 'right';
            }
          } else if (Math.abs(connection.y - offsetY) < delta) {
            resizeMode = 'top';
          } else if (
            Math.abs(connection.y + connection.height - offsetY) < delta
          ) {
            resizeMode = 'bottom';
          }
        } else {
          connection = {
            key: '',
            screenshot: props.screenshotName,
            x: offsetX,
            y: offsetY,
            width: 0,
            height: 0,
          };
          resizeMode = 'bottomRight';
          props.onConnectionsChange!([...props.connections, connection]);
        }
        setActive({
          connection,
          startTouchX: offsetX,
          startTouchY: offsetY,
          resizeMode,
        });
      }}
      onMouseMove={(e: MouseEvent) => {
        const {offsetX, offsetY} = e.nativeEvent;
        if (canvasRef.current && props.onColorPick) {
          const pixel = canvasRef.current.getImageData(offsetX, offsetY, 1, 1);
          const ggg = rgb(pixel);
          props.onColorPick(ggg, false);
        }

        if (!props.onConnectionsChange) {
          return;
        }
        e.preventDefault();
        if (!active.connection || !active.startTouchX || !active.startTouchY) {
          return;
        }
        const deltaX = offsetX - active.startTouchX;
        const deltaY = offsetY - active.startTouchY;
        const {connection} = active;
        if (active.resizeMode === 'topLeft') {
          connection.x = offsetX;
          connection.y = offsetY;
          connection.width -= deltaX;
          connection.height -= deltaY;
        } else if (active.resizeMode === 'bottomLeft') {
          connection.x = offsetX;
          connection.width -= deltaX;
          connection.height += deltaY;
        } else if (active.resizeMode === 'left') {
          connection.x = offsetX;
          connection.width -= deltaX;
        } else if (active.resizeMode === 'topRight') {
          connection.y = offsetY;
          connection.width += deltaX;
          connection.height -= deltaY;
        } else if (active.resizeMode === 'right') {
          connection.width += deltaX;
        } else if (active.resizeMode === 'bottomRight') {
          connection.width += deltaX;
          connection.height += deltaY;
        } else if (active.resizeMode === 'top') {
          connection.y = offsetY;
          connection.height -= deltaY;
        } else if (active.resizeMode === 'bottom') {
          connection.height += deltaY;
        } else {
          connection.x += deltaX;
          connection.y += deltaY;
        }
        setActive({
          ...active,
          connection,
          startTouchX: offsetX,
          startTouchY: offsetY,
        });
      }}
    >
      <Stack direction="row">
        <img
          id="screenshot_main"
          key={`screenshot_${props.onColorPick ? '1' : '0'}`}
          crossOrigin="anonymous"
          src={getScreenshotUrl(props.projectUuid, props.screenshotName)}
          onLoad={() => {
            if (props.onColorPick) {
              const image = document.getElementById(
                'screenshot_main',
              ) as HTMLImageElement;
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d')!;
              canvas.width = image.width;
              canvas.height = image.height;
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
              canvasRef.current = ctx;
            }
          }}
          style={{
            pointerEvents: 'none',
            cursor: 'none',
            maxWidth: 500,
            maxHeight: 800,
          }}
        />
      </Stack>
      {props.connections.map((c, index) => (
        <div
          key={index}
          style={{
            pointerEvents: 'none',
            position: 'absolute',
            left: c.x,
            top: c.y,
            width: c.width,
            height: c.height,
            borderWidth: 2,
            borderStyle: 'solid',
            borderColor:
              active.connection === c || index === props.focusedIndex
                ? 'red'
                : 'yellow',
            ...c.styles,
          }}
        >
          {props.texts && props.texts[index] && (
            <span>{props.texts[index]}</span>
          )}
        </div>
      ))}
    </div>
  );
};

function rgb(pixel: ImageData): string {
  console.log('RGB:', pixel.data);
  const [r, g, b] = pixel.data;

  return (
    '#' +
    r.toString(16).padStart(2, '0') +
    g.toString(16).padStart(2, '0') +
    b.toString(16).padStart(2, '0')
  );
}
